import React from 'react';
import { ActionCard } from './ActionCard';
import { MessageCircle, BookOpen, FolderOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * IdentityActionDock - Container for the 3 primary buttons that form the digital twin.
 * 
 * These are the ONLY official entry points for forming the digital twin:
 * 1. Quick Identity Session → feeds Identity Constitution
 * 2. My Daily Reflection → feeds Temporal Consistency & Identity Mirrors
 * 3. My Knowledge & Documents → feeds Knowledge Layer
 * 
 * No "Create Twin" button. No configuration panels.
 * Identity forms through repetition and time.
 */
export function IdentityActionDock({
  onIdentitySessionClick,
  onReflectionClick,
  onKnowledgeClick,
  hasActiveSession = false
}) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <section 
      className="mb-8 md:mb-12"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="identity-action-dock"
    >
      {/* Subtle header - not a "feature announcement" */}
      <div className="mb-6">
        <p className="text-sm text-gray-500">
          {t('identityDock.subtitle', 'ابدأ من هنا')}
        </p>
      </div>

      {/* The 3 action cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {/* 1. Quick Identity Session */}
        <ActionCard
          title={t('identityDock.identitySession.title', 'جلسة الهوية السريعة')}
          description={t('identityDock.identitySession.description', 'محادثة قصيرة لأفهمك بشكل أفضل. لا نماذج، لا إعدادات.')}
          icon={MessageCircle}
          variant="warm"
          onClick={onIdentitySessionClick}
          data-testid="quick-identity-btn"
        />

        {/* 2. My Daily Reflection */}
        <ActionCard
          title={t('identityDock.reflection.title', 'تأملي اليومي')}
          description={t('identityDock.reflection.description', 'مساحة خاصة للتفكير بصوت عالٍ. لا تقييم، لا تصحيح.')}
          icon={BookOpen}
          variant="sage"
          onClick={onReflectionClick}
          data-testid="daily-reflection-btn"
        />

        {/* 3. My Knowledge & Documents */}
        <ActionCard
          title={t('identityDock.knowledge.title', 'معرفتي ومستنداتي')}
          description={t('identityDock.knowledge.description', 'أضف ملفات أو نصوص تريد أن أتذكرها. أنت تتحكم في ما يُحفظ.')}
          icon={FolderOpen}
          variant="muted"
          onClick={onKnowledgeClick}
          data-testid="knowledge-docs-btn"
        />
      </div>

      {/* Active session indicator - subtle, not intrusive */}
      {hasActiveSession && (
        <div className="mt-4 text-xs text-gray-400 text-center">
          {t('identityDock.hasActiveSession', 'لديك جلسة هوية نشطة')}
        </div>
      )}
    </section>
  );
}

export default IdentityActionDock;
